'use client';

import { useState } from 'react';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';

import { ExpandableSearch, SportsFallbackIcon, SquareIcon, TabButton } from '@/components';
import { useT } from '@/hooks';
import { useAppSelector } from '@/store';

interface SportHeaderProps {
  sportSlug: string;
  activeTab: 'games' | 'props';
  onTabChange: (tab: 'games' | 'props') => void;
  showSpreadsAndTotals?: boolean;
  setShowSpreadsAndTotals?: (show: boolean) => void;
  canShowToggle?: boolean;
  hasGames?: boolean;
  hasProps?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

export const SportHeader = ({
  sportSlug,
  activeTab,
  onTabChange,
  showSpreadsAndTotals = true,
  setShowSpreadsAndTotals,
  canShowToggle = true,
  hasGames = true,
  hasProps = true,
  searchQuery = '',
  onSearchChange,
}: SportHeaderProps) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { t } = useT();
  const { selectedSidebarItem } = useAppSelector((state) => state.sports) || {
    selectedSidebarItem: null,
  };
  const isLive = sportSlug === 'live';

  const handleTabChange = () => {
    onTabChange(activeTab === 'games' ? 'props' : 'games');
  };

  const handleToggleSpreadsAndTotals = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowSpreadsAndTotals?.(event.target.checked);
  };

  const [prevSearchQuery, setPrevSearchQuery] = useState(searchQuery);

  if (searchQuery !== prevSearchQuery) {
    setPrevSearchQuery(searchQuery);
    if (searchQuery) {
      setIsSearchOpen(true);
    }
  }

  const handleSearchToggle = (isOpen: boolean) => {
    setIsSearchOpen(isOpen);
  };

  const handleSearchChange = (query: string) => {
    onSearchChange?.(query);
  };

  const leaguesArray =
    selectedSidebarItem && 'leagues' in selectedSidebarItem
      ? Array.isArray(selectedSidebarItem.leagues)
        ? selectedSidebarItem.leagues
        : [selectedSidebarItem.leagues]
      : [];

  const firstLeague = leaguesArray[0];

  const nameFromItem =
    selectedSidebarItem &&
    ('name' in selectedSidebarItem
      ? selectedSidebarItem.name
      : (selectedSidebarItem as any).category || (selectedSidebarItem as any).sport);
  const sportName =
    sportSlug === 'live'
      ? t('sports.live', { defaultValue: 'Live' })
      : nameFromItem || (sportSlug ? sportSlug.toUpperCase() : '');

  const imageFromItem =
    selectedSidebarItem &&
    ('image' in selectedSidebarItem
      ? selectedSidebarItem.image
      : (selectedSidebarItem as any).img ||
        (firstLeague as any)?.image ||
        (firstLeague as any)?.img);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 4 }}>
          {!isLive && (
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
              }}
            >
              <SquareIcon
                src={imageFromItem}
                alt={sportName}
                size={32}
                borderRadius="8px"
                fallback={<SportsFallbackIcon size={20} />}
              />
            </Box>
          )}
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: 'text.primary' }}>
            {sportName}
          </Typography>
        </Box>

        {isLive && (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', mt: 4 }}>
            <ExpandableSearch
              value={searchQuery}
              onChange={handleSearchChange}
              isOpen={isSearchOpen}
              onToggle={handleSearchToggle}
            />
          </Box>
        )}

        {activeTab === 'games' && !isLive && canShowToggle && (
          <FormControlLabel
            control={
              <Switch
                checked={showSpreadsAndTotals}
                onChange={handleToggleSpreadsAndTotals}
                size="small"
              />
            }
            label={t('sports.showSpreadsAndTotals')}
            sx={{
              '& .MuiTypography-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
              },
              mr: 0,
            }}
          />
        )}
      </Box>

      {!isLive && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasGames && (
              <Box sx={{ display: { xs: isSearchOpen ? 'none' : 'block', sm: 'block' } }}>
                <TabButton
                  label={t('sports.games')}
                  isActive={activeTab === 'games'}
                  onClick={handleTabChange}
                />
              </Box>
            )}
            {hasProps && (
              <Box sx={{ display: { xs: isSearchOpen ? 'none' : 'block', sm: 'block' } }}>
                <TabButton
                  label={t('sports.props')}
                  isActive={activeTab === 'props'}
                  onClick={handleTabChange}
                />
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ExpandableSearch
              value={searchQuery}
              onChange={handleSearchChange}
              isOpen={isSearchOpen}
              onToggle={handleSearchToggle}
            />
          </Box>
        </Box>
      )}
    </>
  );
};
